let localStorageState = null;
const prefix = 'm_';
/**
 * Stores a value in localstorage under a key.
 * @param {string} key - The key to store the value under (without prefix).
 * @param {string} value - The value to be stored.
 * @returns {void}
 */
function setItem(key, value) {
    const _key = prefix + key;
    if (value === undefined || value === null) {
        removeItem(key);
        return;
    }

    if (typeof value === 'object') {
        if (value instanceof Date) {
            window.localStorage.setItem(_key + '_s', value);
        } else {
            window.localStorage.setItem(_key, JSON.stringify(value));
        }
    } else {
        if (typeof value === 'string') {
            window.localStorage.setItem(_key + '_s', value);
        } else {
            window.localStorage.setItem(_key, value);
        }
    }
};

/**
 * Retrieves a value from localStorage with JSON parsing.
 * @param {string} key - The storage key (with prefix).
 * @returns {string | Object | null} - The retirieved value as a string, object or null if not found.
 */
function getItem(key) {
    const _key = prefix + key;
    const stringVal = window.localStorage.getItem(_key + '_s');
    if (stringVal !== null) {
        return stringVal;
    }
    const rawVal = window.localStorage.getItem(_key);
    if (rawVal !== null) {
        try {
            return JSON.parse(rawVal);
        } catch (e) {
            window.sendNodeLog && window.sendNodeLog(`client-error-parse`, window.location.href, 0, e);
            return rawVal;
        }
    }
    return null;
};

/**
 * Removes value of a key from localStorage.
 * @param {string} key - The storage key (with prefix).
 * @returns {void}
 */
function removeItem(key) {
    let _key = prefix + key;
    window.localStorage.removeItem(_key + '_s');
    window.localStorage.removeItem(_key);
};

/**
 * Initializes storage by migrating data from 'movotoLocalStorage' format.
 * Checks for existing storage, parses it, and redistributes the data using setItem.
 * @returns {void}
 */
export function initalStorage() {
    if (window.localStorage) {
        let movotoLocalStorage = window.localStorage.getItem('movotoLocalStorage');
        if (movotoLocalStorage) {
            try {
                movotoLocalStorage = JSON.parse(movotoLocalStorage);
                Object.keys(movotoLocalStorage).forEach((key) => {
                    let value = movotoLocalStorage[key];
                    setItem(key, value);
                });
            } catch (e) {
                movotoLocalStorage = null;
            }
            window.localStorage.removeItem('movotoLocalStorage');
        }
    } else {
        window.sendNodeLog && window.sendNodeLog(`client-error-storage`, window.location.href, 0);
    }
};

/**
 * Sets a storage value with fallback to in-memory localStorageState if localStorage is unavailable.
 * @param {string} key - The storage key.
 * @param {string} value - The value to store.
 * @returns {void}
 */
export function setStorage(key, value) {
    if (window.localStorage) {
        setItem(key, value);
    } else {
        if (!localStorageState) {
            localStorageState = {};
        }
        localStorageState[key] = value;
    }
};

/**
 * Retrieves a storage value with fallback to in-memory localStorageState if localStorage is unavailable.
 * @param {string} key - The storage key.
 * @returns {string | Object | null } - The retirieved value from localStorage or in-memory localStorageState if localStorage is unavailable.
 */
export function getStorage (key) {
    if (window.localStorage) {
        return getItem(key);
    } else {
        if (!localStorageState) {
            localStorageState = {};
        }
        return localStorageState[key];
    }
};

/**
 * Removes a storage value with fallback to in-memory localStorageState if localStorage is unavailable.
 * @param {string} key - The storage key to remove.
 * @returns {void}
 */
export function removeStorage(key) {
    if (window.localStorage) {
        removeItem(key);
    } else {
        if (!localStorageState) {
            localStorageState = {};
            return;
        }
        delete localStorageState[key];
    }
};

/**
 * Sets a value in sessionStorage when available.
 * @param {string} key - The session storage key.
 * @param {string} value - The value to store (should be string or will be converted to string).
 * @returns {void}
 */
export function setSessionStorage(key, value) {
    if (window.sessionStorage) {
        window.sessionStorage.setItem(key, value);
    }
};

/**
 * Retrieves a value from sessionStorage when available.
 * @param {string} key - The session storage key.
 * @returns {string | null} - The stored value as a string or null if not found or sessionStorage unavailable.
 */
export function getSessionStorage(key) {
    if (window.sessionStorage) {
        return window.sessionStorage.getItem(key);
    }
};
